import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ConfigService } from '@app/core/services/config.service';
import { Observable, throwError } from 'rxjs';
import { map, retry, timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { PracticeAgentBehaviourRequest, PracticeAgentBehaviourResponse } from './practice-agent-behaviour.models';

const REQUEST_TIMEOUT_MS = 10000;

@Injectable({ providedIn: 'root' })
export class PracticeAgentBehaviourService {
  private readonly http: HttpClient;
  private readonly url = this.getBackendUrl();
  readonly configured = !!this.url;

  constructor(
    backend: HttpBackend,
    private configService: ConfigService
  ) {
    // Coordination calls use their own client so Clinical API headers and logout handling stay separate.
    this.http = new HttpClient(backend);
  }

  send(request: PracticeAgentBehaviourRequest): Observable<PracticeAgentBehaviourResponse> {
    if (!this.url) {
      return throwError(new Error('Practice Agent behaviour URL is not configured'));
    }

    return this.http.post<PracticeAgentBehaviourResponse>(this.url, request).pipe(
      timeout(REQUEST_TIMEOUT_MS),
      retry(1),
      map(response => {
        if (!this.isValidResponse(response, request.requestId)) {
          throw new Error('Invalid Practice Agent behaviour response');
        }
        return response;
      })
    );
  }

  private getBackendUrl(): string | null {
    try {
      const configuredUrl = this.configService.config.practiceAgent.behaviourUrl;
      if (!configuredUrl) {
        return null;
      }
      const url = new URL(configuredUrl);
      const localHttp = url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname);
      return url.protocol === 'https:' || (!environment.production && localHttp) ? url.href : null;
    } catch {
      return null;
    }
  }

  private isValidResponse(response: PracticeAgentBehaviourResponse, requestId: string): boolean {
    if (!response || response.requestId !== requestId || !['prepare', 'show', 'keep', 'hide', 'none'].includes(response.decision)) {
      return false;
    }
    const pause = response.automaticPause;
    const attempt = response.attempt;
    return (
      Number.isFinite(Date.parse(response.serverTime)) &&
      Number.isFinite(Date.parse(response.nextCheckAt)) &&
      typeof response.policy?.snoozeLabel === 'string' &&
      typeof pause?.active === 'boolean' &&
      (!pause.active || (['close', 'snooze'].includes(pause.kind) && Number.isFinite(Date.parse(pause.until)))) &&
      (attempt === null ||
        (!!attempt?.id &&
          ['automatic', 'manual'].includes(attempt.mode) &&
          ['preparing', 'ready', 'shown', 'expired', 'canceled', 'released', 'empty', 'failed'].includes(attempt.status) &&
          (Number.isFinite(Date.parse(attempt.expiresAt)) ||
            (attempt.mode === 'manual' &&
              attempt.expiresAt === null &&
              (attempt.status === 'shown' || ['hide', 'none'].includes(response.decision)))))) &&
      (!['prepare', 'show', 'keep'].includes(response.decision) || !!attempt) &&
      (!['prepare', 'show'].includes(response.decision) || Number.isFinite(Date.parse(attempt?.expiresAt)))
    );
  }
}
